import React, { FC } from 'react';
import { MessageDialog, useDialogManager } from '../../../../../../widgets/dialog';
import { T } from 'src/shared/helpers';
import { ComposerOptionRowAction, ComposerOptionTreeElement } from '../../../configurator-composer/composer/components/composerOptionTreeElement';
import useComposerStore from '../../../configurator-composer/composer/composerStore';
import ComposerRetargetAttributeDialog from '../../../configurator-composer/composer/dialogs/composerRetargetAttributeDialog';
import { getRetargetObjectsAndMaterials, retargetMapToActions, sortByDisplay } from '../../../configurator-composer/composer/helpers/composerHelpers';
import { carriedFrom, startPointerReorder } from '../../pointerReorder';
import { composerOptionOwner, isComposerOptionAttribute, moveComposerOptions } from './composerMoveOptions';
import { ComposerAttributeNode, ComposerAttributeOptionNode } from './composerAttributesModels';
import { useOptionMoveCommand, useOptionSelectionCommands } from './optionSelectionCommands';
import { composerRowMenuAnchor } from '../composerPickerMenu';
import { isConfirmedRun } from '../../confirmTwice';

// LE OPZIONI STANNO NELL'ALBERO, SOTTO IL LORO ATTRIBUTO (round 4, pacchetto H; utente, 2026-10-14).
// Prima erano un elenco nella pagina dell'attributo, e i dettagli di una opzione aprivano una terza
// colonna: quattro pannelli per arrivare a un'azione. Ora l'albero mostra tutto e l'Inspector è uno.
//
// Le righe parlano con lo store e basta: l'attributo di una riga non è per forza quello scelto, e
// ogni gesto — scegliere, rendere predefinita, spegnere, clonare, togliere, riordinare — dice da sé a
// quale attributo appartiene.
const optionsOf = (attributeID: string) => useComposerStore.getState().attributes.find((attribute) => attribute.id === attributeID)?.options ?? [];

const ComposerOptionRows: FC<{
    attribute: ComposerAttributeNode;
    indent: number;
    highlight?: string;
    /** L'ultima riga premuta senza ⇧ nell'albero: la tiene l'albero, che vede anche gli altri attributi. */
    rangeAnchorRef?: React.MutableRefObject<string | null>;
    /** ⇧click: l'intervallo lo calcola l'albero, sulle righe visibili e oltre questo attributo. */
    onRangeSelect?: (option: ComposerAttributeOptionNode) => void;
    /** Il filtro dell'albero: le opzioni che restano in vista. Senza, tutte. */
    visible?: (option: ComposerAttributeOptionNode) => boolean;
    /** La profondità delle righe nell'albero (quella dell'attributo più uno), per il trascinamento. */
    level?: number;
}> = ({ attribute, indent, highlight, rangeAnchorRef, onRangeSelect, visible, level = 1 }) => {
    const [
        selectedOptions,
        sceneMaterialList,
        sceneMeshList,
        selectAttributes,
        selectOptions,
        selectAreas,
        updateOption,
        removeOption,
        cloneOption,
        setOptionFace,
        validateAttributes
    ] = useComposerStore((store) => [
        store.selectedOptions,
        store.sceneMaterialList,
        store.sceneObjectList,
        store.selectAttributes,
        store.selectOptions,
        store.selectAreas,
        store.updateOption,
        store.removeOption,
        store.cloneOption,
        store.setOptionFace,
        store.validateAttributes
    ]);
    const { showDialog, closeDialog } = useDialogManager();
    const { openCopyPicker, openImagePicker, picker } = useOptionSelectionCommands();
    const { openMoveOptions, picker: movePicker } = useOptionMoveCommand();
    const options = [...attribute.options].filter((option) => !visible || visible(option)).sort((left, right) => sortByDisplay(attribute.options, left, right));
    const selectedIds = (selectedOptions ?? []).filter((option) => option.attributeID === attribute.id).map((option) => option.id);

    // Scegliere un'opzione di un altro attributo sceglie anche il suo attributo: è lui che le contiene.
    const choose = (picked: ComposerAttributeOptionNode[]) => {
        const storeAttribute = useComposerStore.getState().attributes.find((candidate) => candidate.id === attribute.id) ?? attribute;
        const current = useComposerStore.getState().selectedAttributes;
        if (current?.length !== 1 || current[0].id !== attribute.id) selectAttributes([storeAttribute]);
        // UNA SELEZIONE È DI UNA SPECIE SOLA (utente, 2026-10-15): scegliendo un'opzione le aree si spengono.
        selectAreas(null);
        selectOptions(picked);
    };

    const select = (event: React.MouseEvent, option: ComposerAttributeOptionNode) => {
        setOptionFace('actions');
        if (event.shiftKey && onRangeSelect) {
            onRangeSelect(option);
            return;
        }
        if (rangeAnchorRef) rangeAnchorRef.current = option.id;
        if (event.shiftKey && selectedIds.length > 0) {
            const start = options.findIndex((candidate) => candidate.id === selectedIds[selectedIds.length - 1]);
            const end = options.findIndex((candidate) => candidate.id === option.id);
            if (start < 0 || end < 0) return;
            choose(options.slice(Math.min(start, end), Math.max(start, end) + 1));
            return;
        }
        // ⌘click aggiunge o toglie anche un'opzione di un altro attributo: la selezione è una sola.
        if (event.ctrlKey || event.metaKey) {
            const current = useComposerStore.getState().selectedOptions ?? [];
            choose(current.some((candidate) => candidate.id === option.id) ? current.filter((candidate) => candidate.id !== option.id) : [...current, option]);
            return;
        }
        choose([option]);
    };

    const openFace = (option: ComposerAttributeOptionNode, face: 'settings' | 'links') => {
        choose([option]);
        setOptionFace(face);
    };

    const remove = (optionsToRemove: ComposerAttributeOptionNode[]) => {
        const run = () => {
            optionsToRemove.forEach((option) => removeOption(option.attributeID, option.id));
            selectOptions([]);
            void validateAttributes();
        };
        // Dal menu si chiede due volte, senza finestra (confirmTwice): arrivati qui, la domanda è già fatta.
        if (isConfirmedRun()) return run();
        showDialog(
            'confirmDialog',
            <MessageDialog
                title={T._('Confirm Removal', 'Admin')}
                message={T._('Are you sure you want to remove the selected option(s)? ', 'Admin') + optionsToRemove.map((option) => option.name).join(', ')}
                buttons={[
                    {
                        label: T._('Remove', 'Admin'),
                        destructive: true,
                        onClick: () => {
                            closeDialog('confirmDialog');
                            run();
                        }
                    }
                ]}
            />
        );
    };

    const retarget = (optionsToRetarget: ComposerAttributeOptionNode[]) => {
        const actions = optionsToRetarget.flatMap((option) => option.actions || []);
        const { objectIds, materialIds } = getRetargetObjectsAndMaterials(actions);
        showDialog(
            'composerRetargetAttribute',
            <ComposerRetargetAttributeDialog
                materialsList={sceneMaterialList}
                objectsList={sceneMeshList}
                usedMaterialsList={sceneMaterialList.filter((material) => materialIds.includes(material.id))}
                usedObjectsList={sceneMeshList.filter((mesh) => objectIds.includes(mesh.id) || objectIds.includes(mesh.name || ''))}
                onConfirm={(data) => {
                    closeDialog('composerRetargetAttribute');
                    if (!data) return;
                    const remapped = retargetMapToActions(actions, data.materialMap, data.objectMap);
                    optionsToRetarget.forEach((option) =>
                        updateOption({
                            ...option,
                            actions: option.actions.map((action) => remapped.find((candidate) => candidate.id === action.id) ?? action)
                        })
                    );
                }}
            />
        );
    };

    // IL «…» DI UNA RIGA SCELTA INSIEME AD ALTRE VALE PER TUTTA LA SELEZIONE, come nella struttura.
    const subjectOf = (option: ComposerAttributeOptionNode) => {
        const all = selectedOptions ?? [];
        return all.length > 1 && all.some((candidate) => candidate.id === option.id) ? all : [option];
    };

    const onRowAction = (action: ComposerOptionRowAction | 'edit', option: ComposerAttributeOptionNode) => {
        if (action === 'edit') return openFace(option, 'settings');
        if (action === 'move') return openMoveOptions(composerRowMenuAnchor(option.id), subjectOf(option));
        if (action === 'copy' || action === 'replace') return openCopyPicker(composerRowMenuAnchor(option.id), subjectOf(option), action);
        if (action === 'image') return openImagePicker(composerRowMenuAnchor(option.id), subjectOf(option));
        if (action === 'links') return openFace(option, 'links');
        if (action === 'duplicate') {
            const clones = subjectOf(option).map((subject) => cloneOption(subject));
            choose(clones);
            setOptionFace('settings');
            return;
        }
        if (action === 'retarget') return retarget(subjectOf(option));
        remove(subjectOf(option));
    };

    const toggleEnabled = (option: ComposerAttributeOptionNode) => {
        const current = optionsOf(attribute.id).find((candidate) => candidate.id === option.id) ?? option;
        updateOption({ ...current, enabled: current.enabled === false });
    };

    const setDefault = (option: ComposerAttributeOptionNode) => {
        useComposerStore.setState((store) => {
            const index = store.attributes.findIndex((candidate) => candidate.id === attribute.id);
            if (index < 0) return store;
            const updatedAttribute = {
                ...store.attributes[index],
                options: store.attributes[index].options.map((candidate) => ({ ...candidate, isDefault: candidate.id === option.id }))
            };
            return {
                ...store,
                attributes: store.attributes.map((candidate, attributeIndex) => (attributeIndex === index ? updatedAttribute : candidate)),
                selectedAttributes: store.selectedAttributes?.map((candidate) => (candidate.id === updatedAttribute.id ? updatedAttribute : candidate)) ?? null,
                selectedOptions:
                    store.selectedOptions?.map((candidate) =>
                        candidate.attributeID === attribute.id ? { ...candidate, isDefault: candidate.id === option.id } : candidate
                    ) ?? null
            };
        });
    };

    const move = (option: ComposerAttributeOptionNode, direction: 'up' | 'down') => {
        const index = options.findIndex((candidate) => candidate.id === option.id);
        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        if (swapIndex < 0 || swapIndex >= options.length) return;
        const swap = options[swapIndex];
        updateOption({ ...option, displayOrder: swap.displayOrder });
        updateOption({ ...swap, displayOrder: option.displayOrder });
    };

    // IL TRASCINAMENTO (pointerReorder): un'opzione si riordina nel suo attributo o cambia casa (utente,
    // 2026-10-15) — davanti o dopo un'opzione di un altro attributo, o dentro la sua riga, in coda. Se è
    // scelta porta con sé tutta la selezione, anche quella sotto altri attributi, nell'ordine dell'albero.
    const startDrag = (event: React.PointerEvent<HTMLElement>, dragged: ComposerAttributeOptionNode) => {
        const chosen = (useComposerStore.getState().selectedOptions ?? []).map((option) => option.id);
        const scope = event.currentTarget.closest('[data-reorder-scope]');
        const treeOrder = Array.from(scope?.querySelectorAll<HTMLElement>('[data-reorder-id]') ?? []).map((row) => row.dataset.reorderId ?? '');
        // Le scelte sotto un attributo chiuso non sono righe a vista: partono anche loro, in fondo.
        const carriedIds = carriedFrom([...treeOrder, ...chosen.filter((id) => !treeOrder.includes(id))], dragged.id, chosen);
        startPointerReorder(event, {
            fromRow: true,
            draggedId: dragged.id,
            carriedIds,
            canDrop: (targetId, position) => {
                if (carriedIds.includes(targetId)) return false;
                const attributes = useComposerStore.getState().attributes;
                if (position === 'into') return attributes.some((candidate) => candidate.id === targetId && isComposerOptionAttribute(candidate));
                return !!composerOptionOwner(targetId);
            },
            onDrop: (targetId, position) => {
                const target =
                    position === 'into' ? useComposerStore.getState().attributes.find((candidate) => candidate.id === targetId) : composerOptionOwner(targetId);
                if (!target) return;
                const moved = moveComposerOptions(carriedIds, target.id, position === 'into' ? null : { id: targetId, position });
                const storeTarget = useComposerStore.getState().attributes.find((candidate) => candidate.id === target.id) ?? target;
                selectAttributes([storeTarget]);
                selectAreas(null);
                selectOptions(moved);
                void validateAttributes();
            }
        });
    };

    return (
        <>
            {options.map((option) => (
                <ComposerOptionTreeElement
                    key={option.id}
                    option={option}
                    indent={indent}
                    highlight={highlight}
                    selected={selectedIds.includes(option.id)}
                    inSelection={(selectedOptions?.length ?? 0) > 1 && selectedIds.includes(option.id)}
                    hasLink={option.links.length > 0}
                    onAction={onRowAction}
                    onDoubleClick={(_, picked) => openFace(picked, 'settings')}
                    level={level}
                    onRowPointerDown={startDrag}
                    onMoveDown={() => move(option, 'down')}
                    onMoveUp={() => move(option, 'up')}
                    onSelect={select}
                    onSetDefault={setDefault}
                    onVisibilityClick={toggleEnabled}
                />
            ))}
            {picker}
            {movePicker}
        </>
    );
};

export default ComposerOptionRows;
